/**
 * Project Detail Page
 * 
 * Phase D3: Read-only status and extraction state.
 * ΔD6: Extraction failure propagation
 * Displays project metadata, associated documents, extraction status.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  getProject, 
  getDocument,
  type RbProject, 
  type RbDocument,
  type RbJob
} from '@threadlock/data';
import { ROUTES } from '@threadlock/routing';
import { useWorkspaceState } from '../../config/workspace';

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { activeWorkspaceId } = useWorkspaceState();
  const workspaceId = activeWorkspaceId;
  const [project, setProject] = useState<RbProject | null>(null);
  const [documents, setDocuments] = useState<RbDocument[]>([]);
  const [extractionJobs, setExtractionJobs] = useState<RbJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;

    const db = getFirestore();

    // Load project
    getProject(db, projectId)
      .then(async (proj) => {
        if (!proj) {
          setError('Project not found');
          setLoading(false);
          return;
        }

        setProject(proj);

        // Load associated documents
        const docPromises = proj.sourceDocumentIds.map(docId => getDocument(db, docId));
        const docs = await Promise.all(docPromises);
        setDocuments(docs.filter((d): d is RbDocument => d !== null));

        // Load extraction jobs for this project
        // Note: This is a simplified version - in Phase D6 we'll have proper project-level job queries
        setExtractionJobs([]);
        
        setLoading(false);
      })
      .catch((err) => {
        console.error('[ProjectDetailPage] Error loading project:', err);
        setError(err instanceof Error ? err.message : 'Failed to load project');
        setLoading(false);
      });
  }, [projectId]);

  const handleExtractQuestions = async () => {
    if (!projectId || !workspaceId || !project) return;

    setExtracting(true);
    setExtractionError(null);

    try {
      const functions = getFunctions();
      const extractQuestions = httpsCallable(functions, 'rbExtractQuestions');
      
      await extractQuestions({
        workspaceId,
        projectId,
        sourceDocumentIds: project.sourceDocumentIds,
      });

      // Reload project to get updated status
      const db = getFirestore();
      const updatedProject = await getProject(db, projectId);
      if (updatedProject) {
        setProject(updatedProject);
      }

      setExtracting(false);
    } catch (err) {
      console.error('[ProjectDetailPage] Extraction failed:', err);
      setExtractionError(err instanceof Error ? err.message : 'Extraction failed');
      setExtracting(false);

      // Reload project to get failed status
      const db = getFirestore();
      const updatedProject = await getProject(db, projectId);
      if (updatedProject) {
        setProject(updatedProject);
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-neutral-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error || 'Project not found'}</p>
        </div>
      </div>
    );
  }

  const lastExtractionJob = extractionJobs[0]; // Most recent
  const canExtract = project.status === 'created';
  const canReview = project.status === 'ready';
  const hasFailed = project.status === 'failed';

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">{project.title}</h1>
            <p className="mt-2 text-sm text-neutral-600">
              {project.jurisdiction} • {project.intent}
            </p>
          </div>
          <div className="flex gap-2">
            {canExtract && (
              <button
                onClick={handleExtractQuestions}
                disabled={extracting}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-neutral-300 disabled:cursor-not-allowed"
              >
                {extracting ? 'Extracting...' : 'Extract Questions'}
              </button>
            )}
            {canReview && (
              <Link
                to={ROUTES.PROJECT_QUESTIONS.replace(':projectId', project.id)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Review Questions
              </Link>
            )}
          </div>
        </div>

        {/* ΔD6: Extraction failure notice */}
        {hasFailed && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-red-800 mb-1">Extraction Failed</h3>
            <p className="text-sm text-red-700">
              Question extraction encountered an error. Please check your source documents and try again.
            </p>
            <button
              onClick={handleExtractQuestions}
              disabled={extracting}
              className="mt-3 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {extracting ? 'Retrying...' : 'Retry Extraction'}
            </button>
          </div>
        )}

        {/* Extraction error display */}
        {extractionError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800">{extractionError}</p>
          </div>
        )}

        {/* Status card */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-neutral-600">Project Status</p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  project.status === 'ready'
                    ? 'bg-green-100 text-green-800'
                    : project.status === 'extracting'
                    ? 'bg-yellow-100 text-yellow-800'
                    : project.status === 'failed'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-neutral-100 text-neutral-800'
                }`}
              >
                {project.status}
              </span>
            </div>
            <div>
              <p className="text-sm text-neutral-600">Last Extraction</p>
              <p className="text-sm text-neutral-900">
                {lastExtractionJob 
                  ? lastExtractionJob.createdAt.toDate().toLocaleString()
                  : 'Not yet started'}
              </p>
            </div>
          </div>
        </div>

        {/* Associated documents */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">
            Associated Documents ({documents.length})
          </h2>
          
          {documents.length === 0 ? (
            <p className="text-sm text-neutral-600">No documents found.</p>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg">
                  <div>
                    <Link
                      to={ROUTES.LIBRARY_DOCUMENT.replace(':documentId', doc.id)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      {doc.filename}
                    </Link>
                    <p className="text-xs text-neutral-500 mt-1">
                      {doc.documentType} • {doc.jurisdiction} • {doc.policyTier}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      doc.status === 'ready'
                        ? 'bg-green-100 text-green-800'
                        : doc.status === 'processing'
                        ? 'bg-yellow-100 text-yellow-800'
                        : doc.status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-neutral-100 text-neutral-800'
                    }`}
                  >
                    {doc.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}